import { autoinject, bindable, Disposable, processContent } from 'aurelia-framework';
import { EventAggregator } from 'aurelia-event-aggregator';
import { debounce, microTask } from 'app/shared/lib/decorators';
import { focusFirst } from 'app/shared/lib/focus';
import { guid } from 'app/shared/lib/guid';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { getKey, Key } from 'app/shared/lib/keys';
import { TabsBase, TabClass, TabSelector, TAB_ATTRIBUTE_MAP, isTabActive, getTabWidth } from 'app/shared/elements/tabs-base';
import { getAttributeBinding } from 'app/shared/lib/binding';
import { changeTag } from 'app/shared/lib/dom';

const TEST_NAME_ATTRIBUTE = 'test-name';

@autoinject
@processContent(EssHorizontalNavMenu.processContent)
export class EssHorizontalNavMenu extends TabsBase {

    ////****************** Fields and Properties ************************************////
    @bindable
    public label: string | null = null;

    @bindable
    public routableElement: Element | null = null;

    ////****************** Event Subscriptions **************************************////
    private navigationCompletedEvent: Disposable | null = null;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly events: EventAggregator,
        element: Element,
        logger: AppLogger) {
        super(element, logger);
    }

    protected attached(): void {
        this.tabObserver.observe(this.$tablist, { childList: true });
        this.resizeObserver.observe(this.element, {}); // recompute overflow when tab bar is resized
        this.recomputeTabs();

        this.navigationCompletedEvent = this.events.subscribe('router:navigation:complete', () => this.navigationComplete());

        this.navigationComplete();
    }

    protected detached(): void {
        if(this.navigationCompletedEvent) {
            this.navigationCompletedEvent.dispose();
            this.navigationCompletedEvent = null;
        }
        this.tabObserver.disconnect();
        this.resizeObserver.disconnect();
    }

    ////****************** Events ***************************************************////
    protected handleKeydown(event: KeyboardEvent): true | void {
        if(!this.focusedTab) return;

        // gather all tabs (including those grouped inside a dropdown) to get an accurate focus tab index
        const allTabs: Element[] = [
            ...Array.from(this.$leftTabGroup.querySelectorAll(TabSelector.GroupedTab)),
            ...this.tabs.filter(tab => tab.getAttribute('hidden') === null),
            ...Array.from(this.$rightTabGroup.querySelectorAll(TabSelector.GroupedTab))
        ];
        let focusedTabIndex = allTabs.indexOf(this.focusedTab);

        const maxLeftGroupIndex = this.startGroup.length - 1;
        const minRightGroupIndex = this.tabs.length - this.endGroup.length;
        const maxIndex = this.tabs.length - 1;

        // left and right grouping tabs have index -1: check if either are focused
        const groupingTabFocused = this.focusedTab === this.$leftGroupingTab || this.focusedTab === this.$rightGroupingTab;
        const leftGroupingTabFocused = this.focusedTab === this.$leftGroupingTab;
        if(groupingTabFocused) focusedTabIndex = leftGroupingTabFocused ? maxLeftGroupIndex : minRightGroupIndex;

        let openDropdown = false;
        switch(getKey(event)) {
            case Key.Up:
                if(groupingTabFocused) {
                    // if grouping tab focused, get ready to open dropdown and focus the appropriate last item
                    openDropdown = true;
                    focusedTabIndex = leftGroupingTabFocused ? maxLeftGroupIndex : maxIndex;
                } else {
                    if(!groupingTabFocused && !this.groupDropdownOpened) return;
                    // otherwise, decrement focus index, cycling within the appropriate range
                    focusedTabIndex--;
                    if(this.groupDropdownOpened && focusedTabIndex < 0) focusedTabIndex = maxLeftGroupIndex; // within left dropdown range
                    else if(this.groupDropdownOpened && focusedTabIndex < minRightGroupIndex && focusedTabIndex > maxLeftGroupIndex) focusedTabIndex = maxIndex; // within right dropdown range
                    else if(focusedTabIndex < 0) focusedTabIndex = maxIndex; // within overall range
                }
                break;
            case Key.Down:
                if(groupingTabFocused) {
                    // if grouping tab focused, get ready to open dropdown and focus the appropriate first item
                    openDropdown = true;
                    focusedTabIndex = leftGroupingTabFocused ? 0 : minRightGroupIndex;
                } else {
                    if(!groupingTabFocused && !this.groupDropdownOpened) return;
                    // otherwise, increment focus index, cycling within the appropriate range
                    focusedTabIndex++;
                    if(this.groupDropdownOpened && focusedTabIndex > maxIndex) focusedTabIndex = minRightGroupIndex; // within left dropdown range
                    else if(this.groupDropdownOpened && focusedTabIndex < minRightGroupIndex && focusedTabIndex > maxLeftGroupIndex) focusedTabIndex = 0; // within right dropdown range
                    else if(focusedTabIndex >= this.tabs.length) focusedTabIndex = 0; // within overall range
                }
                break;
            case Key.Left:
                // decrement focus index while treating grouped tabs as one
                if(focusedTabIndex <= maxLeftGroupIndex) focusedTabIndex = maxIndex;
                else if(focusedTabIndex >= minRightGroupIndex) focusedTabIndex = minRightGroupIndex - 1;
                else focusedTabIndex = (this.tabs.length + focusedTabIndex - 1) % this.tabs.length;
                if(this.groupDropdownOpened) this.toggleGroupDropdown();
                break;
            case Key.Right:
                // increment focus index while treating grouped tabs as one
                if(focusedTabIndex <= maxLeftGroupIndex) focusedTabIndex = maxLeftGroupIndex + 1;
                else if(focusedTabIndex >= minRightGroupIndex) focusedTabIndex = 0;
                else focusedTabIndex = (focusedTabIndex + 1) % this.tabs.length;
                if(this.groupDropdownOpened) this.toggleGroupDropdown();
                break;
            case Key.Esc:
                // close dropdowns
                if(this.groupDropdownOpened) this.toggleGroupDropdown();
                break;
            case Key.Home:
                // move focus to first index of all tabs or, if right dropdown is opened, first index of dropdown
                if(this.groupDropdownOpened && focusedTabIndex > maxLeftGroupIndex) focusedTabIndex = minRightGroupIndex
                else focusedTabIndex = 0;
                break;
            case Key.End:
                // move focus to last index of all tabs or, if left dropdown is opened, last index of dropdown
                if(this.groupDropdownOpened && focusedTabIndex < maxLeftGroupIndex) focusedTabIndex = maxLeftGroupIndex;
                else focusedTabIndex = maxIndex;
                break;
            case Key.Enter:
            case Key.Space:
                if(groupingTabFocused) {
                    // if grouping tab focused, get ready to open dropdown and focus the appropriate first item
                    openDropdown = true;
                    focusedTabIndex = leftGroupingTabFocused ? 0 : minRightGroupIndex;
                } else {
                    return this.focusedTab?.click();
                }
                break;
            default:
                // if a character key was pressed...
                if(event.key.length === 1 && event.key.match(/\S/)) {
                    let foundIndex: number = -1;
                    // search for the next match after currently focused index
                    for(let i = 0; i < allTabs.length; i++) {
                        if(allTabs[i].textContent?.trim().toLowerCase()[0] === event.key.toLowerCase()) {
                            // if match not found after current index, search from beginning
                            if(foundIndex < 0 || i > focusedTabIndex) foundIndex = i;
                            if(i > focusedTabIndex && foundIndex > -1) break;
                        }
                    }
                    if(foundIndex === -1) return true;
                    // handle matches found in either the left or right dropdown
                    if(foundIndex <= maxLeftGroupIndex || foundIndex >= minRightGroupIndex) {
                        this.focusedTab = foundIndex <= maxLeftGroupIndex ? this.$leftGroupingTab : this.$rightGroupingTab;
                        if(!this.groupDropdownOpened) {
                            openDropdown = true;
                        } else if(focusedTabIndex >= minRightGroupIndex && foundIndex <= maxLeftGroupIndex) {
                            // if jumping from a match in the right dropdown to a match in the left dropdown
                            this.toggleGroupDropdown();
                            this.focusedTab = this.$leftGroupingTab;
                            openDropdown = true;
                        } else if(focusedTabIndex <= maxLeftGroupIndex && foundIndex >= minRightGroupIndex) {
                            // if jumping from a match in the left dropdown to a match in the right dropdown
                            this.toggleGroupDropdown();
                            this.focusedTab = this.$rightGroupingTab;
                            openDropdown = true;
                        }
                    }
                    focusedTabIndex = foundIndex;
                } else {
                    return true;
                }
        }

        // open dropdown if needed
        if(openDropdown) this.toggleGroupDropdown(this.focusedTab);

        let focusedTab: HTMLElement | null = null;
        let tabGroup: HTMLElement | null = null;
        if(focusedTabIndex <= maxLeftGroupIndex) {
            // if group dropdown opened, focus the tab within the dropdown, otherwise focus the left grouping tab
            focusedTab = this.groupDropdownOpened ? <HTMLElement>this.$leftTabGroup.children[focusedTabIndex] : this.$leftGroupingTab;
            tabGroup = this.groupDropdownOpened ? this.$leftTabGroup : null;
        } else if(focusedTabIndex >= minRightGroupIndex) {
            // if group dropdown opened, focus the tab within the dropdown, otherwise focus the right grouping tab
            focusedTab = this.groupDropdownOpened ? <HTMLElement>this.$rightTabGroup.children[focusedTabIndex - minRightGroupIndex] : this.$rightGroupingTab;
            tabGroup = this.groupDropdownOpened ? this.$rightTabGroup : null;
        } else {
            // otherwise just focus the ungrouped tab
            focusedTab = this.tabs[focusedTabIndex];
        }

        this.updateFocus(focusedTab, tabGroup);
        // then focus the tab, delaying if we need to wait for a dropdown to open
        this.focusedTab.focus();
        if(!openDropdown) this.focusedTab.focus();
        else window.setTimeout(() => this.focusedTab!.focus(), 150); // 150ms corresponds to 'quick' animation
    }

    protected navigationComplete(): void {
        // if the active tab remains hidden in a dropdown after navigation completes, recompute tabs to bring it back out
        if(this.activeTab && !this.tabs.filter(tab => tab.getAttribute('hidden') === null).includes(this.activeTab)) this.recomputeTabs();
        // focus the first focusable element below the navbar, if specified
        if(this.routableElement) this.focusRoutableElement(this.routableElement);
    }

    ////****************** Functions ************************************************////
    @microTask
    private focusRoutableElement(routableElement: Element): void {
        focusFirst(routableElement);
    }

    @debounce()
    protected recomputeTabs(): void {
        this.$tablist.classList.add(TabClass.Processing);
        this.tabs = Array.from(this.element.querySelectorAll<HTMLElement>(TabSelector.Tab));
        this.tabWidths = this.tabs.reduce((map, tab) => {
            if(!map.has(tab)) {
                map.set(tab, getTabWidth(tab));
            }
            return map;
        }, this.tabWidths);
        if(this.activeTab === null || !this.tabs.includes(this.activeTab)) {
            const activeTab: HTMLElement | undefined = this.tabs.find((tab) => isTabActive(tab));
            if(activeTab !== undefined) {
                this.activateTab(activeTab);
            }
        }
        this.$tablist.classList.remove(TabClass.Processing);
        this.consolidateTabs();
    }

    @debounce()
    protected consolidateTabs(): void {
        const { activeTab, tabs, tabWidths, $tablist } = this;

        // INFO [Matt] Triggered by active, which is attached before tabs is attached.
        if(!tabs || !tabs.includes(activeTab as HTMLElement) || !activeTab) return;

        const activatedIndex: number = tabs.indexOf(activeTab);
        const activeTabWidth: number = tabWidths.get(activeTab)!;
        const maxTabIndex = tabs.length - 1;
        const groupLeft: HTMLElement[] = [];
        const groupRight: HTMLElement[] = [];
        let availableSpace = $tablist.clientWidth - activeTabWidth;

        // for each tab to the left and right of the activated tab
        for(let i = 1; i <= Math.max(activatedIndex, maxTabIndex - activatedIndex); i++) {
            const previousTabIndex = activatedIndex - i;
            const subsequentTabIndex = activatedIndex + i;
            const previousTabExists = previousTabIndex >= 0;
            const subsequentTabExists = subsequentTabIndex <= maxTabIndex;
            const previousTabWidth = previousTabExists ? tabWidths.get(tabs[previousTabIndex])! : 0;
            const subsequentTabWidth = subsequentTabExists ? tabWidths.get(tabs[subsequentTabIndex])! : 0;

            // if adding leftTab and rightTab would overflow
            const previousFits = previousTabWidth < availableSpace;
            const subsequentFits = subsequentTabWidth < availableSpace;

            // if both fit, update space and continue
            if(previousTabWidth + subsequentTabWidth < availableSpace) {
                availableSpace -= previousTabWidth;
                availableSpace -= subsequentTabWidth;
            }

            // if neither fits, collapse both
            else if(!previousFits && !subsequentFits) {
                if(previousTabExists) groupLeft.push(...tabs.slice(0, previousTabIndex + 2));
                if(subsequentTabExists) groupRight.push(...tabs.slice(subsequentTabIndex - 1));
                break;
            }

            // left does not fit, or either fits and right fits better, collapse left
            else if(!previousFits || subsequentFits && (previousTabIndex === 0 || previousTabWidth > subsequentTabWidth)) {
                if(previousTabExists) groupLeft.push(...tabs.slice(0, Math.max(previousTabIndex + 2, 2)));
                if(subsequentTabExists) groupRight.push(...tabs.slice(subsequentTabIndex));
                break;
            }

            // right does not fit, or either fits and left fits better, or neither fits better, collapse right
            else if(!subsequentFits || previousFits && (subsequentTabIndex === maxTabIndex || subsequentTabWidth >= previousTabWidth)) {
                if(previousTabExists) groupLeft.push(...tabs.slice(0, previousTabIndex + 1));
                if(subsequentTabExists) groupRight.push(...tabs.slice(Math.min(subsequentTabIndex - 1, maxTabIndex - 1)));
                break;
            }
        }

        // safeguard against the grouping of singular tabs
        this.startGroup = groupLeft.length > 1 && groupLeft.map((tab) => Object.assign({ tab, content: tab.innerHTML })) || [];
        this.endGroup = groupRight.length > 1 && groupRight.map((tab) => Object.assign({ tab, content: tab.innerHTML })) || [];
        if(!this.startGroup.length) groupLeft.splice(0);
        if(!this.endGroup.length) groupRight.splice(0);

        tabs.forEach(tab => tab.toggleAttribute('hidden', groupLeft.includes(tab) || groupRight.includes(tab)));
    }

    protected static processContent(_compiler: any, _resources: any, el: Element, _instruction: any): true {
        // for each <ess-tab>
        const tabs = Array.from(el.querySelectorAll(TabClass.Tab));
        tabs.forEach((tab, index) => {

            const hasRepeater = tab.hasAttribute('repeat.for');
            let tabId = getAttributeBinding(tab, 'id') ?? guid();
            if(hasRepeater) {
                tabId += '-${$index}';
            }

            // transform the header
            let tabHeader = tab.querySelector('ess-tab-header') || tab.querySelector('[slot="header"]');
            if(tabHeader === null) {
                tabHeader = document.createElement('a');
            } else {
                if(/ess-tab-header/i.test(tabHeader.tagName)) {
                    tabHeader = changeTag(tabHeader, 'a');
                }
                if(Array.from(tabHeader.attributes).some(({ name, value }) => /^header/.test(name))) {
                    // TODO [Matt] Static, so we can't reference this.logger. Fix.
                    console.warn('A tab header element or slot may be overwritten by a header attribute or binding.', tabHeader);
                }
                tabHeader.remove();
            }

            // normalize the header
            Array.from(tab.attributes).forEach(({ name, value }) => {
                if(/^header/.test(name)) {
                    tabHeader!.innerHTML = name === 'header' ? value : `\${${value}}`;
                }
                else {
                    Array.from(TAB_ATTRIBUTE_MAP).some(([match, newName]) => {
                        const doesMatch = name.startsWith(match);
                        if(doesMatch) {
                            name = name.replace(match, newName);
                        }
                        return doesMatch;
                    });
                    tabHeader!.setAttribute(name, value);
                }
            });

            // test attributes
            const testName = tabHeader.getAttribute(TEST_NAME_ATTRIBUTE);
            let testId: string = '';
            if(testName) {
                // if specified test name lacks `nav-` prefix, add it
                testId = /^nav-/.test(testName) ? testName : `nav-${testName}`;
            } else {
                // Add repeat index to item index
                testId = hasRepeater ? `nav-\${${index} + $index}` : `nav-${index}`;
            }
            tabHeader.setAttribute('test-id', testId);

            if(!tabHeader.getAttribute('id')) { tabHeader.setAttribute('id', tabId); }
            tabHeader.setAttribute('slot', 'header');
            tabHeader.setAttribute('role', 'menuitem');
            tabHeader.setAttribute('tabindex', '-1');
            tabHeader.classList.add(TabClass.Tab);
            el.append(tabHeader);

            tab.remove();
        });
        return true;
    }
}
